import { REPORT_PAGE_HEIGHT } from '../../PageConfigProvider/constants';
import { REPORT_FLOW_COVER_SECTION_ID } from '../convert-grid-report-to-flow';
import type {
  ReportFlowBlock,
  ReportFlowDocument,
  ReportFlowLeafBlock,
} from '../report-flow-types';

const PRINT_PAGE_HEIGHT_PX = 1056;
const PRINT_MARGIN_TOP_PX = 72;
const PRINT_MARGIN_BOTTOM_PX = 96;
const PRINT_BLOCK_GAP_PX = 24;
export const PRINT_CONTENT_HEIGHT_PX =
  PRINT_PAGE_HEIGHT_PX - PRINT_MARGIN_TOP_PX - PRINT_MARGIN_BOTTOM_PX;

const DEFAULT_LEAF_HEIGHT_PX = 420;
const PRINT_HEIGHT_SCALE = PRINT_CONTENT_HEIGHT_PX / REPORT_PAGE_HEIGHT;

const toPrintLeaf = (leaf: ReportFlowLeafBlock): ReportFlowLeafBlock => {
  const { startsPage: _startsPage, ...rest } = leaf;
  if (leaf.kind === 'text') return rest;
  return {
    ...rest,
    heightPx: Math.min(
      Math.round(
        (leaf.heightPx ?? DEFAULT_LEAF_HEIGHT_PX) * PRINT_HEIGHT_SCALE
      ),
      PRINT_CONTENT_HEIGHT_PX
    ),
  };
};

export const toPrintBlock = (block: ReportFlowBlock): ReportFlowBlock => {
  if (block.kind !== 'columns') return toPrintLeaf(block);
  const { startsPage: _startsPage, ...rest } = block;
  return { ...rest, columns: block.columns.map(toPrintLeaf) };
};

export const reportFlowBlocks = (doc: ReportFlowDocument): ReportFlowBlock[] =>
  doc.sections.flatMap((section) => section.blocks);

type MeasuredBlock = {
  id: string;
  heightPx: number;
  startsPage?: boolean;
  cutOffsetsPx?: number[];
};

export type PrintPageEntry = {
  id: string;
  slice?: { offsetPx: number; heightPx: number };
};

const nextCut = (
  cutOffsetsPx: number[],
  offsetPx: number,
  contentHeightPx: number
) => {
  const limit = offsetPx + contentHeightPx;
  const fitting = cutOffsetsPx.filter((cut) => cut > offsetPx && cut <= limit);
  return fitting.length > 0 ? Math.max(...fitting) : limit;
};

export const paginatePrintBlocks = (
  blocks: MeasuredBlock[],
  {
    contentHeightPx = PRINT_CONTENT_HEIGHT_PX,
    gapPx = PRINT_BLOCK_GAP_PX,
  }: { contentHeightPx?: number; gapPx?: number } = {}
): PrintPageEntry[][] => {
  const pages: PrintPageEntry[][] = [];
  let current: PrintPageEntry[] = [];
  let used = 0;

  const closePage = () => {
    if (current.length > 0) pages.push(current);
    current = [];
    used = 0;
  };

  for (const block of blocks) {
    if (block.heightPx > contentHeightPx) {
      closePage();
      let offsetPx = 0;
      while (block.heightPx - offsetPx > contentHeightPx) {
        const cut = nextCut(
          block.cutOffsetsPx ?? [],
          offsetPx,
          contentHeightPx
        );
        pages.push([
          { id: block.id, slice: { offsetPx, heightPx: cut - offsetPx } },
        ]);
        offsetPx = cut;
      }
      const remainder = block.heightPx - offsetPx;
      current.push({
        id: block.id,
        slice: { offsetPx, heightPx: remainder },
      });
      used = remainder;
      continue;
    }

    const overflows = used + gapPx + block.heightPx > contentHeightPx;
    if (current.length > 0 && (block.startsPage || overflows)) closePage();
    used += (current.length > 0 ? gapPx : 0) + block.heightPx;
    current.push({ id: block.id });
  }
  closePage();
  return pages;
};

export type PrintContentsEntry = {
  label: string;
  anchorId: string;
  pageNumber: number;
};

const blockOwnsAnchor = (block: ReportFlowBlock, anchorId: string) =>
  block.anchorId === anchorId ||
  (block.kind === 'columns' &&
    block.columns.some((column) => column.anchorId === anchorId));

export const buildPrintContents = (
  doc: ReportFlowDocument,
  pages: PrintPageEntry[][],
  firstContentPageNumber: number
): PrintContentsEntry[] => {
  const pageIndexByBlockId = new Map<string, number>();
  pages.forEach((entries, pageIndex) => {
    for (const { id } of entries) {
      if (!pageIndexByBlockId.has(id)) pageIndexByBlockId.set(id, pageIndex);
    }
  });
  const blocks = reportFlowBlocks(doc);

  const entries: PrintContentsEntry[] = [];
  for (const entry of doc.toc) {
    if (entry.sectionId === REPORT_FLOW_COVER_SECTION_ID) continue;
    const section = doc.sections.find(
      (candidate) => candidate.anchorId === entry.anchorId
    );
    const block =
      section?.blocks[0] ??
      blocks.find((candidate) => blockOwnsAnchor(candidate, entry.anchorId));
    const pageIndex = block ? pageIndexByBlockId.get(block.id) : undefined;
    if (pageIndex === undefined) continue;
    entries.push({
      label: entry.label,
      anchorId: entry.anchorId,
      pageNumber: firstContentPageNumber + pageIndex,
    });
  }
  return entries;
};
